// src/components/Inquiry/hooks/useEmailPreview.ts
import { useEffect, useState } from "react";
import { CartType } from "types/CartTypes";
import { OrderType } from "types/OrderTypes";
import { CreateInquiryItemType } from "types/InquiryTypes";
import { PartnerType } from "types/partners/PartnerTypes";
import { apiPreviewInquiry } from "util/network/Inquiries";
import { InquiryFormValues } from "../types";
import { buildInquiryPayload } from "../utils/inquiryPayload";

type UseEmailPreviewParams = {
  formValues: InquiryFormValues;
  mergedItems: CreateInquiryItemType[];
  partner: PartnerType | undefined;
  prodObj: OrderType | CartType | undefined;
  prodType: "order" | "cart";
  oldInquiryId?: number;
  token: string;
};

type UseEmailPreviewResult = {
  html: string;
  isOpen: boolean;
  downloadPDF: () => void;
};

/**
 * Debounced email preview. Auto-fetches HTML whenever form values or items
 * change, with or without a supplier - `partner` is kept as a dependency so
 * picking one re-renders the email under their name. PDF download uses the
 * same payload.
 */
export function useEmailPreview({
  formValues,
  mergedItems,
  partner,
  prodObj,
  prodType,
  oldInquiryId,
  token,
}: UseEmailPreviewParams): UseEmailPreviewResult {
  const [html, setHtml] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // EE-253: no longer gated on a supplier. An Inquiry raised from a
    // Requisition has none until one is picked off the shortlist, and a
    // shortlist may hold several - so waiting for one meant the preview never
    // appeared at all. What is being previewed is the common part of the
    // email; the backend renders a placeholder where the supplier's name goes.
    const timeout = setTimeout(() => {
      const payload = buildInquiryPayload(
        formValues,
        mergedItems,
        prodObj,
        prodType,
        oldInquiryId
      );
      apiPreviewInquiry(token, payload, "html")
        .then((res) => res.text())
        .then((text) => {
          setHtml(text);
          setIsOpen(true);
        })
        .catch(console.error);
    }, 500);

    return () => clearTimeout(timeout);
    // Every value buildInquiryPayload reads. The effect closed over prodObj
    // and prodType without listing them, so picking a supplier first and an
    // order second left the previously fetched standalone HTML on screen:
    // buildInquiryPayload branches entirely on prodObj, and nothing here told
    // the effect it had changed. The only thing that could re-fire it was
    // mergedItems changing identity, and useInquiryItems' own effect runs in
    // the same commit *before* this one - so at the commit where prodObj
    // first becomes defined, mergedItems is still the old reference.
    //
    // Primitives rather than formValues itself, to avoid re-rendering on
    // object reference churn.
  }, [
    partner,
    prodObj,
    prodType,
    formValues.type,
    formValues.partnerId,
    formValues.deadline,
    formValues.deliveryMethod,
    formValues.message,
    formValues.title,
    formValues.deliveryTime,
    formValues.notes,
    mergedItems,
  ]);

  const downloadPDF = () => {
    const payload = buildInquiryPayload(
      formValues,
      mergedItems,
      prodObj,
      prodType,
      oldInquiryId
    );
    apiPreviewInquiry(token, payload, "pdf")
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" })
        );
        window.open(url, "_blank");
        window.URL.revokeObjectURL(url);
      })
      .catch(console.error);
  };

  return { html, isOpen, downloadPDF };
}
